import { useAppSystem } from "@/global/hooks/useAppSystem";
import { useAppStore } from "@/global/store";
import {
  eventsState,
  selectEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { ActionModal } from "@/shared/base/components/ActionModal/ActionModal";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { Image } from "@/shared/base/components/Image/Image";
import { convertDate, convertTime } from "@/global/utils/dateTimeConvert";
import { useCartHelpers } from "../useCartHelpers";
import { getEventsBelowMaxLimit } from "../utils.eventValidators";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { EventType } from "@/global/store/data/types";
import { purchaseSeatsTabUIDs } from "@/global/config/constants";
import { useMinEventCount } from "../useMinEventCount";
import { createState } from "@/global/store/zustand_store";
import { throttleTime } from "@/global/utils/utils";

const useModalState = createState({
  isOpen: false,
  maxLimitEvents: [] as EventType[],
});

export const useMaxLimitErrorModal = () => {
  const modalState = useModalState();
  const open = (args: { maxLimitEvents: EventType[] }) => {
    modalState.set({
      isOpen: true,
      maxLimitEvents: args.maxLimitEvents,
    });
  };
  return { ...modalState, open };
};

export function MaxLimitErrorModal() {
  const modalState = useMaxLimitErrorModal();
  const { router, teamConfig } = useAppSystem();
  const { teamSchema } = teamConfig;
  const { minGameSelectionCount } = useMinEventCount();

  // states
  const selectedEvents = useAppStore(selectEventsSelected);

  const { sm: smDown } = useCssMediaQuery("down");
  const { dispatch } = useAppSystem();
  const { setCartActiveEventUrl } = useCartHelpers();

  const handleClose = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => {
      modalState.reset();
    });
  };

  const handleBack = () => {
    handleClose();
    const newSelectedEvents = getEventsBelowMaxLimit(
      selectedEvents,
      teamConfig
    );
    dispatch(eventsState.actions.setEventsSelected(newSelectedEvents));
    router.push({
      query: { purchaseSeatTab: purchaseSeatsTabUIDs.findGames, teamSchema },
    });
  };

  const handleRemoveAndContinue = () => {
    handleClose();
    const newSelectedEvents = getEventsBelowMaxLimit(
      selectedEvents,
      teamConfig
    );
    dispatch(eventsState.actions.setEventsSelected(newSelectedEvents));
    setCartActiveEventUrl(newSelectedEvents[0], true);
  };

  return (
    <ActionModal
      hideCloseIcon
      isOpen={modalState.isOpen}
      onClose={handleRemoveAndContinue}
      renderBody={() => {
        return (
          <Box
            width={"100%"}
            minHeight={"300px"}
            maxHeight={"600px"}
            // height={"600px"}
            overflow={"auto"}
          >
            <Box p={3}>
              <Box>
                <Typography variant="body1" color="text.secondary">
                  You have claimed the maximum number of seats allowed for this
                  event. Please select another event to continue.
                </Typography>
              </Box>

              <Box mt={3}>
                <Grid container spacing={2}>
                  {modalState.maxLimitEvents.map((evt) => {
                    return (
                      <Grid item xs={12} key={evt.eventCode}>
                        <MaxLimitEvent
                          logoUrl={evt.logoUrl}
                          title={evt.title}
                          date={evt.date}
                          time={evt.time}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <>
            <Divider />
            <Stack
              p={2}
              direction={smDown ? "column" : "row"}
              justifyContent={"flex-end"}
              spacing={2}
              width="100%"
            >
              <ModalActionButton
                variant="secondary"
                onClick={() => handleBack()}
              >
                Pick Another Game
              </ModalActionButton>

              {selectedEvents.length > minGameSelectionCount ? (
                <ModalActionButton
                  variant="primary"
                  onClick={() => handleRemoveAndContinue()}
                >
                  Remove & Continue
                </ModalActionButton>
              ) : null}
            </Stack>
          </>
        );
      }}
    />
  );
}

interface MaxLimitEventProps {
  logoUrl: string;
  title: string;
  date: string;
  time: string;
}

function MaxLimitEvent(props: MaxLimitEventProps) {
  const { theme } = useAppSystem();
  return (
    <Box
      sx={{
        border: `1px solid  ${theme.palette.divider}`,
        borderRadius: "8px",
      }}
    >
      <Box p={1} py={1}>
        <Stack
          direction={"row"}
          justifyContent={"flex-start"}
          alignItems={"center"}
          spacing={2}
        >
          <Box>
            {props.logoUrl ? (
              <Image
                src={props.logoUrl}
                alt={"StadiumBackground team logo"}
                width={48}
                height={48}
              />
            ) : null}
          </Box>

          <Box textAlign={"left"}>
            <Typography fontSize={"1rem"} fontWeight={700}>
              {props.title}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />
      <Box
        display={"flex"}
        alignItems={"center"}
        padding={"1rem"}
        gap={"0.5rem"}
      >
        <Typography
          variant="body1"
          color="text.secondary"
          fontSize={"0.9rem"}
          fontWeight={"500"}
        >
          {convertDate(props.date)}
        </Typography>
        <Divider orientation="vertical" variant="middle" flexItem />
        <Typography
          variant="body1"
          color="text.secondary"
          fontSize={"0.9rem"}
          fontWeight={"500"}
        >
          {convertTime(props.time)}
        </Typography>
      </Box>
    </Box>
  );
}
